import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import FormLabel from '@mui/joy/FormLabel';
import FormHelperText from '@mui/joy/FormHelperText';
import Autocomplete from '@mui/joy/Autocomplete';
import CircularProgress from '@mui/joy/CircularProgress';
import Button from '@mui/joy/Button';
import _ from 'lodash';
import { readLocations } from '../API/location';
import { getLocationDisplayName } from '../utils/locationDisplayName';
import { PERMISSIONS } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';

const defaultControlSx = {
    backgroundColor: '#f7f9fc',
    borderRadius: '8px',
    padding: '4px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    '&:hover': { borderColor: '#5f09c2' },
    '&:focus': { borderColor: '#5f09c2', outline: 'none', boxShadow: '0 0 6px rgba(0, 123, 255, 0.5)' },
    '&[disabled]': { backgroundColor: '#e9ecef', color: '#6c757d', cursor: 'not-allowed' },
};

export default function LocationOptionList({
    onChange,
    required,
    defaultValue,
    disabled = false,
    label,
    size,
    errorMessage,
    sx: sxProp = {},
    slotProps = {},
}) {
    const { t, i18n } = useTranslation('ui');
    const { hasPermission } = usePermissions();
    const canListLocations = hasPermission(PERMISSIONS.LOCATIONS_LIST);
    const resolvedLabel = label ?? t('widgets.location');
    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);

    const handleInputChange = (event, newInputValue) => {
        setInputValue(newInputValue);
        if (!newInputValue) {
            fetchLocations();
        }
    };

    const fetchLocations = async (searchTerm = '') => {
        if (!canListLocations) {
            setOptions([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const response = await readLocations(searchTerm, '');
            // Ensure we always set an array, even if the API returns unexpected data
            const results = response && response.data && response.data.results;
            setOptions(Array.isArray(results) ? results : []);
        } catch (error) {
            console.error('Failed to fetch locations:', error.message);
            // Set empty array on error to prevent filter error
            setOptions([]);
        } finally {
            setLoading(false);
        }
    };

    const debouncedFetchLocations = useCallback(_.debounce(fetchLocations, 300), []);

    useEffect(() => {
        if (inputValue) {
            debouncedFetchLocations(inputValue);
        }
    }, [inputValue, debouncedFetchLocations]);

    useEffect(() => {
        if (open) {
            fetchLocations();
        }
    }, [open]);

    const controlSx = { ...defaultControlSx, ...sxProp };

    return (
        <Box
            sx={{
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                boxSizing: 'border-box',
                marginBottom: slotProps.root?.sx?.marginBottom ?? 2,
                position: 'relative',
                ...(slotProps.root?.sx || {}),
                '&:focus-within': {
                    boxShadow: '0 0 8px rgba(0, 128, 255, 0.3)',
                    borderRadius: controlSx.borderRadius || '8px',
                },
            }}
        >
            <FormLabel
                sx={{
                    fontWeight: 600,
                    color: '#555',
                    marginBottom: '6px',
                    fontSize: '14px',
                    ...(slotProps.label?.sx || {}),
                }}
            >
                {required ? `${resolvedLabel} *` : resolvedLabel}
            </FormLabel>
            <Autocomplete
                size={size}
                onInputChange={handleInputChange}
                value={defaultValue == null || defaultValue === '' ? null : defaultValue}
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                placeholder={t('widgets.searchLocations')}
                options={Array.isArray(options) ? options : []}
                loading={loading}
                disabled={disabled || !canListLocations}
                onChange={onChange}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                getOptionLabel={(option) => {
                    if (!option) return '';
                    const label = getLocationDisplayName(option, i18n.language);
                    if (label) return label;
                    if (option.id != null) return `#${option.id}`;
                    return '';
                }}
                noOptionsText={
                    !canListLocations ? (
                        <span>
                            {t('permissions.noPermission', {
                                defaultValue: 'You do not have permission to perform this action.',
                            })}
                        </span>
                    ) : (
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        width="100%"
                        px={2}
                    >
                        <span>{t('widgets.noLocationsFound')}</span>
                        <Button
                            variant="solid"
                            color="primary"
                            size="sm"
                            onClick={() => window.open('/settings/locations', '_blank')}
                        >
                            {t('widgets.addLocation')}
                        </Button>
                    </Box>
                    )
                }
                loadingText={t('actions.loading')}
                filterOptions={(opts) => opts}
                endDecorator={
                    loading && (
                        <CircularProgress size="sm" sx={{ bgcolor: 'background.surface' }} />
                    )
                }
                sx={{
                    width: '100%',
                    maxWidth: '100%',
                    minWidth: 0,
                    boxSizing: 'border-box',
                    ...controlSx,
                    ...(errorMessage
                        ? {
                              borderColor: 'var(--joy-palette-danger-500, #c41c1c)',
                              boxShadow: '0 0 0 1px var(--joy-palette-danger-500, #c41c1c)',
                          }
                        : {}),
                }}
            />
            {errorMessage ? (
                <FormHelperText sx={{ color: 'danger.500', mt: 0.5 }}>{errorMessage}</FormHelperText>
            ) : null}
        </Box>
    );
}
